import { useCallback } from 'react';
import uuid from 'uuid/v4';

import { type DocNode } from '@atlaskit/adf-schema';

import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';

import type { DynamicUiSource } from './common-types.ts';

/**
 * Hook that provides a callback to send selected text to the AIFC staging modal
 * as a Rovo chat message, skipping the prompt screen and going straight to the
 * staging area preview.
 *
 * This is used by the Remix feature to hand off option selection
 * to the AIFC Create with Rovo flow.
 */
export const useRemixToAIFC = () => {
	const { sendMessage, createConversationOptimistically, abortRequests, activeConversation } =
		useSmartCreationChatStoreApi();
	const { setPrompt, setDynamicUiContext } = useStagingAreaActions();
	const { preferences } = useUserPreferences();
	const { openModal } = useSmartCreationModalActions();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();

	const isRovoResponding = activeConversation?.status === CHAT_STORE_STATUS.typingResponse;

	const stopRemixing = useCallback(() => {
		abortRequests?.({ conversationId: activeConversation?.id });
	}, [abortRequests, activeConversation?.id]);

	const openAIFCStagingModal = useCallback(() => {
		// Open the Confluence Smart Creation Modal with stagingAreaActive=true
		// to skip the prompt screen and go straight to the staging area preview.
		// A no-op submitCallback is passed so that handleSubmit in ConfluenceSmartCreationModal
		// invokes this callback instead of navigating away from the current page.
		openModal({
			entryPoint: 'dynamicUi',
			spaceKey: spaceKey ?? undefined,
			stagingAreaActive: true,
			isFloating: true,
			shouldShowBackButton: false,
			content: contentId
				? {
						id: contentId,
						type: contentType ?? 'page',
						subType: isLivePage ? 'live' : null,
					}
				: undefined,
			submitCallback: async () => {},
		});
	}, [openModal, spaceKey, contentId, contentType, isLivePage]);

	const sendAIFCPrompt = useCallback(
		async ({
			userPrompt,
			dynamicUiType,
			dynamicUiSource,
		}: {
			userPrompt?: string;
			dynamicUiType: RemixOptionSelectionParameters['type'];
			dynamicUiSource: DynamicUiSource | undefined;
		}) => {
			const promptText = userPrompt !== undefined ? userPrompt : 'Remix the selected content';

			// Build the ADF DocNode prompt
			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text: promptText,
							},
						],
					},
				],
			};

			const newConversationId = uuid();
			await createConversationOptimistically({ id: newConversationId });

			// Send the message to Rovo
			sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: newConversationId,
				messageSources: {
					interactionSource: 'smartCreate',
				},
				productKey: 'confluence',
				contentContext: 'staging-area',
				// Only send minionAlias if the feature gate is not enabled
				...(fg('confluence_disable_remix_minion_alias') ? {} : { minionAlias: 'remix-content' }),
				creationContextParams: {
					dynamicUiType,
					dynamicUiSource,
					source: 'remix',
				},
			});

			// Store the prompt text for display in the staging area header
			setPrompt(promptText);

			// Persist the dynamic UI context so the update flow can use
			// the original type/source when sending remix adjustment messages
			setDynamicUiContext(dynamicUiType, dynamicUiSource);
		},
		[createConversationOptimistically, sendMessage, setPrompt, setDynamicUiContext, preferences.storeUserMessages],
	);

	return { sendAIFCPrompt, stopRemixing, isRovoResponding, openAIFCStagingModal };
};
